import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form541ShcD = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Schedule D (541) Instructions 2025: Capital Gain or Loss"
          description="Learn how to complete California Schedule D (541) for 2025 with clear line-by-line instructions for capital gains, losses, carryovers, and limitations."
          keywords=""
          canonical="resources/tax-exempt/schedule-d-541/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Schedule D (541)</span>
          </span>{' '}
          Instructions (2025): <br />
          California Capital Gain or Loss
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 1,
          }}
        >
          Understand what to report on each line of California Schedule D (541),
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          from capital gains and losses to carryovers and limitations.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=schedule-d-541`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/schedule-d-541/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-09-21T09:00:00-05:00"
                      >
                        September 21, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-09-21T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule D (541) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          What is Schedule D (541)?
                        </strong>{' '}
                        California Schedule D (541), Capital Gain or Loss, is
                        used to
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          report gains and losses
                        </strong>{' '}
                        from the sale or exchange of capital assets.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Who is required to file?{' '}
                        </strong>{' '}
                        Estates, trusts, and{' '}
                        <Link href="/tax-exempt/" sx={{ color: '#2020ff' }}>
                          tax-exempt
                        </Link>{' '}
                        organizations that have capital gains or losses that
                        must be reported on{' '}
                        <strong style={{ fontWeight: 600 }}> Form 541 </strong>{' '}
                        or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          <Link
                            href="/resources/tax-exempt/form-109-instructions/"
                            sx={{ color: '#2020ff' }}
                          >
                            {' '}
                            Form 109
                          </Link>
                        </strong>
                        .{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          What does it report?{' '}
                        </strong>{' '}
                        It reports capital asset sales or exchanges,
                        installment-sale gains, pass-through gains or losses,
                        capital gain distributions, and capital loss carryovers.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          What is the 2025 capital loss limitation?{' '}
                        </strong>{' '}
                        If applicable, the allowable capital loss is generally
                        limited to the smaller of the net loss or{' '}
                        <strong style={{ fontWeight: 600 }}>$3,000</strong>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          What about capital loss carryover?{' '}
                        </strong>{' '}
                        The accompanying worksheet determines the amount of
                        unused capital loss carried from 2025 to 2026.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Complete Schedule D (541): Line-by-Line
                          Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part1" sx={{ color: '#0202ff' }}>
                          Part I - Capital Gain and Loss
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part2" sx={{ color: '#0202ff' }}>
                          Part II – Summary of Part I
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part3" sx={{ color: '#0202ff' }}>
                          Part III – Computation of Capital Loss Limitation
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How to Complete Schedule D (541): Line-by-Line Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/how-to-complete-schedule-d-541.webp" />
                  <source srcSet="/assets/images/how-to-complete-schedule-d-541.png" />
                  <Box
                    component="img"
                    src="/assets/images/how-to-complete-schedule-d-541.png"
                    loading="lazy"
                    alt="Schedule D (541) for Capital Gain or Loss -  organization basic deatils"
                    width={760}
                    height={100}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Name as shown on the tax return
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the organization, estate or trust as it
                  appears on the associated CA{' '}
                  <strong style={{ fontWeight: 600 }}> Form 541 </strong> or CA{' '}
                  <strong style={{ fontWeight: 600 }}>
                    <Link
                      href="/tax-exempt/efile-ftb-ca-form109-online/"
                      sx={{ color: '#2020ff' }}
                    >
                      Form 109
                    </Link>
                    .
                  </strong>{' '}
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For example </b>
                  <br />{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Name as shown on the tax return:
                  </strong>{' '}
                  Bright Horizons Youth Development Center
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  FEIN
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the federal{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    employer identification number (FEIN){' '}
                  </strong>{' '}
                  associated with the entity and its related tax return.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For example </b>
                  <br /> <strong style={{ fontWeight: 600 }}>FEIN:</strong>{' '}
                  12-3456789 
                </Alert>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips :</b> Ensure that the name and FEIN are consistent
                  with those on Form 541 or Form 109. Consistent identification
                  will help ensure that the schedule is filed with the right tax
                  return. Part I - Capital Gain and Loss
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part1"
                >
                  Part I - Capital Gain and Loss
                </Typography>
                <picture>
                  <source srcSet="/assets/images/capital-gain-and-loss.webp" />
                  <source srcSet="/assets/images/capital-gain-and-loss.png" />
                  <Box
                    component="img"
                    src="/assets/images/capital-gain-and-loss.png"
                    loading="lazy"
                    alt="Schedule D (541) Part I - Capital Gain and Loss, Line 1"
                    width={760}
                    height={531}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 1 – Sales or Exchanges of Capital Assets
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Report each sale or exchange of a capital asset during the
                    2025 taxable year in line 1. {' '}
                  </li>
                  <li>
                    The 2025 Schedule D (541) provides six columns for each
                    transaction:{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      description of property, date acquired, date sold, gross
                      sales price, cost or other basis plus expenses of sale,
                      and gain or loss
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (a) – Description of Property{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a description of the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    capital asset that was sold or exchanged
                  </strong>
                  . The description should be specific enough to identify the
                  property.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (b) – Date Acquired{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the capital asset was{' '}
                  <strong style={{ fontWeight: 600 }}> acquired </strong> using
                  the format{' '}
                  <strong style={{ fontWeight: 600 }}> mm/dd/yyyy</strong>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (c) – Date Sold{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the capital asset was{' '}
                  <strong style={{ fontWeight: 600 }}>sold or exchanged</strong>
                  , using the{' '}
                  <strong style={{ fontWeight: 600 }}> mm/dd/yyyy </strong>{' '}
                  format.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (d) – Gross Sales Price
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    gross sales price{' '}
                  </strong>
                  of the capital asset. This represents the gross amount
                  associated with the sale before accounting for the applicable
                  expense of sale reported in{' '}
                  <strong style={{ fontWeight: 600 }}> Column (e)</strong>.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (e) – Cost or Other Basis, as Adjusted, Plus Expense of
                  Sale
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the property's{' '}
                    <strong style={{ fontWeight: 600 }}>
                      cost or other basis, as adjusted, plus the expense of sale
                    </strong>
                    .{' '}
                  </li>
                  <li>This column therefore combines; </li>
                </Box>
                <Box
                  sx={{
                    py: 2,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e5f6fd',
                    borderRadius: 2,
                    textAlign: 'center',
                    color: '#014361',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Adjusted cost or other basis + applicable expense of
                    sale{' '}
                  </Typography>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  <br />{' '}
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,

                      '& li': {
                        mb: 1.5,
                      },
                    }}
                  >
                    <li>
                      Adjusted basis:{' '}
                      <strong style={{ fontWeight: 600 }}> $14,000 </strong>
                    </li>
                    <li>
                      Expense of sale:{' '}
                      <strong style={{ fontWeight: 600 }}> $500 </strong>
                    </li>
                  </Box>
                  So, Column (e) is equal to
                  <strong style={{ fontWeight: 600 }}> $14,500 </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (f) – Gain or Loss{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the gain or loss by subtracting{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Column (e) from Column (d)
                  </strong>
                  .{' '}
                </Typography>
                <Box
                  sx={{
                    py: 2,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e5f6fd',
                    borderRadius: 2,
                    textAlign: 'center',
                    color: '#014361',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Formula:
                    <br />
                    Gain or Loss = Column (d) − Column (e)
                  </Typography>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  <br />{' '}
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,

                      '& li': {
                        mb: 1.5,
                      },
                    }}
                  >
                    <li>
                      Gross sales price (Column d):{' '}
                      <strong style={{ fontWeight: 600 }}> $20,000 </strong>
                    </li>
                    <li>
                      Adjusted basis + expense of sale (Column e):{' '}
                      <strong style={{ fontWeight: 600 }}> $14,500 </strong>
                    </li>
                    <li>
                      Gain:{' '}
                      <strong style={{ fontWeight: 600 }}> $5,500 </strong>
                    </li>
                  </Box>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    $20,000 − $14,500 = $5,500(gain){' '}
                  </strong>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  For Example:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here is an example as quick reference for filing Line 1 of
                  Part I in Schedule D (541),
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (a) Description of property
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (b) Date acquired
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (c) Date sold
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (d) Gross sales price
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (e) Cost or other basis, as adjusted, plus expense
                            of sale
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            (f) Gain (or loss)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            100 shares of ABC Corporation common stock{' '}
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            03/15/2020{' '}
                          </TableCell>

                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            08/10/2025
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $20,000{' '}
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $14,500 (includes $14,000 basis + $500 selling
                            expense){' '}
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontWeight: 600 }}>
                              {' '}
                              $5,500 gain{' '}
                            </strong>{' '}
                            (($20,000 − $14,500)){' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <picture>
                  <source srcSet="/assets/images/capital-gain-and-loss-line-2-to-line-8.webp" />
                  <source srcSet="/assets/images/capital-gain-and-loss-line-2-to-line-8.png" />
                  <Box
                    component="img"
                    src="/assets/images/apital-gain-and-loss-line-2-to-line-8.png"
                    loading="lazy"
                    alt="Schedule D (541) Part I - Capital Gain and Loss, Line 2 to Line 8"
                    width={760}
                    height={117}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 2 – Capital Gain From Installment Sales
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      capital gain from installment sales{' '}
                    </strong>{' '}
                    reported on{' '}
                    <strong style={{ fontWeight: 600 }}>FTB 3805E</strong>.{' '}
                  </li>
                  <li>
                    If an estate or trust sold property at a gain during the tax
                    year and will receive a payment in a later tax year, the
                    installment method generally applies.{' '}
                  </li>
                  <li>
                    If the estate or trust elects out of the installment method,
                    report the gain or loss on{' '}
                    <strong style={{ fontWeight: 600 }}> Line 1 </strong>{' '}
                    instead.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 3 – Net Gain or Loss From Pass-Through Entities
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    share of net gain or loss{' '}
                  </strong>{' '}
                  from:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>S corporations</li>
                  <li>Fiduciaries</li>
                  <li>Partnerships</li>
                  <li>Limited liability companies (LLCs) </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example:</b>
                  <br /> If the applicable Schedule K-1 or other supporting
                  information shows your share of capital gain from a
                  partnership as{' '}
                  <strong style={{ fontWeight: 600 }}> $4,000</strong>, enter
                  <strong style={{ fontWeight: 600 }}> $4,000 on Line 3</strong>
                  .{' '}
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 4 – Capital Gain Distributions
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    capital gain distributions reported in federal{' '}
                    <Link
                      href="/resources/information-returns/form-1099-div-instructions/"
                      sx={{ color: '#2020ff' }}
                    >
                      Form 1099-DIV
                    </Link>
                  </strong>
                  , refer to <strong style={{ fontWeight: 600 }}>Box 2a</strong>
                  .{' '}
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  <br /> If{' '}
                  <strong style={{ fontWeight: 600 }}> Form 1099-DIV</strong>,
                  Box 2a is <strong style={{ fontWeight: 600 }}> $2,500</strong>
                  , then{' '}
                  <strong style={{ fontWeight: 600 }}>Schedule D (541)</strong>,{' '}
                  Line 4 is also{' '}
                  <strong style={{ fontWeight: 600 }}>$2,500</strong>{' '}
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 5 – Net Gain or Loss
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 5 combines the gain or loss amounts from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 1, Column (f), and Lines 2, 3, and 4
                  </strong>
                  .{' '}
                </Typography>
                <Box
                  sx={{
                    py: 2,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e5f6fd',
                    borderRadius: 2,
                    textAlign: 'center',
                    color: '#014361',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Calculation:
                    <br />
                    Line 5 = Line 1, Column (f) + Line 2 + Line 3 + Line 4
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  For Example:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Item
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'right',
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 1, Column (f){' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $5,500{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 2{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $2,000{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 3{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $1,500{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 4{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $1,000{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Line 5{' '}
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'right', fontWeight: 600 }}
                          >
                            $10,000{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 6 – Gain From California Schedule D-1
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable{' '}
                  <strong style={{ fontWeight: 600 }}>
                    gain from California Schedule D-1
                  </strong>
                  , Sales of Business Property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 7 – Capital Loss Carryover form Prior-Year
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the amount of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      unused capital loss carryover from prior years
                    </strong>
                    . For the 2025 Schedule D (541), Line 7 specifically refers
                    to the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      capital loss carryover from 2024
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    There is{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      no capital loss carryover{' '}
                    </strong>{' '}
                    from a{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      decedent to an estate
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 8 – Net Gain or Loss
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 8 calculates the final net gain or loss for Part I by
                  combining{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 5, Line 6, and Line 7
                  </strong>
                  .{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  For Example:{' '}
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Item
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'right',
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 5{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $10,000{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 6{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $3,000{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Line 7{' '}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            $(4,000){' '}
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Line 8{' '}
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'right', fontWeight: 600 }}
                          >
                            $9,000{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  id="part2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  Part II – Summary of Part I
                </Typography>
                <picture>
                  <source srcSet="/assets/images/541-part-ii-summary-of-part-i-line-9.webp" />
                  <source srcSet="/assets/images/541-part-ii-summary-of-part-i-line-9.png" />
                  <Box
                    component="img"
                    src="/assets/images/541-part-ii-summary-of-part-i-line-9.png"
                    loading="lazy"
                    alt="Schedule D (541) Part II – Summary of Part I, Line 9 "
                    width={760}
                    height={81}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 9 – Allocation of Capital Gain or Loss
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Use <strong style={{ fontWeight: 600 }}> Line 9 </strong> to
                    summarize the net gain or loss calculated on{' '}
                    <strong style={{ fontWeight: 600 }}>Part I</strong>, Line 8,
                    and allocate the amount between{' '}
                    <strong style={{ fontWeight: 600 }}> beneficiaries </strong>{' '}
                    and the{' '}
                    <strong style={{ fontWeight: 600 }}> fiduciary</strong>. If
                    Column (c) shows a net loss, complete Part III.
                  </li>
                  <li>
                    Line 9 has three columns:{' '}
                    <strong style={{ fontWeight: 600 }}>
                      (a) Beneficiaries, (b) Fiduciary, and (c) Total
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (a) – Beneficiaries{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the amount of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      capital gain or loss allocable to the beneficiaries
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Generally, do{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      not allocate capital losses to beneficiaries
                    </strong>{' '}
                    unless the losses are required to offset capital gains.
                  </li>
                  <li>
                    If this is the{' '}
                    <strong style={{ fontWeight: 600 }}> final year </strong> of
                    the estate or trust, a capital loss carryover may be allowed
                    to beneficiaries to the extent of their distributive
                    shares.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Nonresident and part-year resident beneficiaries
                  may have different reporting requirements for loss carryovers,
                  deferred deductions, and deferred income.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (b) – Fiduciary{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the amount of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      capital gain or loss allocable to the fiduciary
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Also include in Column (b) any{' '}
                    <strong style={{ fontWeight: 600 }}>
                      capital gain paid or permanently set aside for charitable
                      purposes under IRC Section 642(c)
                    </strong>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (c) – Total{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                >
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      total of Columns (a) and (b)
                    </strong>
                    .{' '}
                  </li>
                  <li>Where Column (c) = Column (a) + Column (b)</li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  For Example:{' '}
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (a) Beneficiaries
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (b) Fiduciary
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            (c) Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'right',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $6,000{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'right',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $4,000{' '}
                          </TableCell>

                          <TableCell sx={{ textAlign: 'right' }}>
                            $10,000{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="part3"
                >
                  Part III – Computation of Capital Loss Limitation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/541-part-iii-computation-of-capital-loss-limitation-line-10.webp" />
                  <source srcSet="/assets/images/541-part-iii-computation-of-capital-loss-limitation-line-10.png" />
                  <Box
                    component="img"
                    src="/assets/images/541-part-iii-computation-of-capital-loss-limitation-line-10.png"
                    loading="lazy"
                    alt="Schedule D (541) Part III – Computation of Capital Loss Limitation, Line 10"
                    width={760}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Line 10 – Capital Loss Limitation
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete <strong style={{ fontWeight: 600 }}>Line 10</strong>{' '}
                  when{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Line 9, Column (c), shows a net loss
                  </strong>
                  . Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    smaller of the net loss on Line 9, Column (c), or
                    $3,000{' '}
                  </strong>{' '}
                  as a loss on Line 10.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <b>For Example: </b>
                  <br /> If Line 9, Column (c) shows a
                  <strong style={{ fontWeight: 600 }}> $5,000 net loss</strong>:
                  <Box
                    component="ul"
                    sx={{ mb: 2, ml: 2.5, '& li': { mb: 1.3 } }}
                  >
                    <li>
                      Net loss on Line 9, Column (c):{' '}
                      <strong style={{ fontWeight: 600 }}> $5,000 </strong>{' '}
                    </li>
                    <li>
                      Capital loss limitation:{' '}
                      <strong style={{ fontWeight: 600 }}> $3,000 </strong>{' '}
                    </li>
                    <li>
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Line 10: $3,000 loss{' '}
                      </strong>{' '}
                    </li>
                  </Box>
                  If Line 9, Column (c) shows a{' '}
                  <strong style={{ fontWeight: 600 }}>$2,000 net loss</strong>,
                  enter <strong style={{ fontWeight: 600 }}>$2,000</strong>,{' '}
                  because it is less than the $3,000 limit.
                </Alert>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The given information is based on the official{' '}
                        <strong style={{ fontWeight: 600 }}>
                          California Franchise Tax Board (FTB) 2025 Schedule D
                          (541)
                        </strong>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          <Link
                            href="https://www.ftb.ca.gov/forms/2025/2025-541-d-instructions.html"
                            target="_blank"
                            sx={{ color: '#2020ff' }}
                          >
                            Instructions
                          </Link>
                        </strong>{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="faq"
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. My estate sold property in 2025 but will receive some of
                  the payment in 2026. Which line should I use?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    If the estate uses the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      installment method
                    </strong>
                    , report the applicable capital gain on{' '}
                    <strong style={{ fontWeight: 600 }}>Line 2</strong> using
                    the amount calculated on{' '}
                    <strong style={{ fontWeight: 600 }}>FTB 3805E</strong>. If
                    the estate elects out of the installment method, report the
                    gain or loss on{' '}
                    <strong style={{ fontWeight: 600 }}>Line 1</strong> instead.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. My Schedule D (541) shows a $5,000 net capital loss. Can I
                  claim the entire $5,000?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    {' '}
                    <strong style={{ fontWeight: 600 }}>No.</strong> If{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Line 9, Column (c)
                    </strong>{' '}
                    shows a net loss, Line 10 is limited to the smaller of the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      net loss or $3,000
                    </strong>
                    . The amount not allowed on Line 10 may be included in the
                    calculation of the capital loss carryover to the following
                    year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. My trust has a capital loss from a previous year. How do I
                  use it on the 2025 Schedule D (541)?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    Enter the applicable{' '}
                    <strong style={{ fontWeight: 600 }}>
                      unused capital loss carryover from prior years on Line 7
                    </strong>
                    . For the 2025 form, Line 7 specifically identifies the
                    carryover from 2024. A capital loss cannot be carried over
                    from a decedent to an estate.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form541ShcD;
